/**
 * Templates list for the Templates accordion.
 *
 * Groups the response of `api/DocumentManagement/get-templates-by-language-code`
 * by `DocClassAndDocName` so each document class collapses/expands as a section.
 *
 * Each row inside a group shows: [FileType icon] [FormatSize] [Title]
 * The top search input filters on DocClassAndDocName / Format / Title
 * (i.e. "Class / Format / Description") and matches groups + items.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ContextualMenu, IconButton, Label, Stack, mergeStyleSets } from "@fluentui/react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Close";
import { useTheme } from "../../../../theme/OAS365Theme";
import { useCommonControlsStyle } from "../../../../theme/styles/CommonControlsStyle";
import { getFileIconPath, getDisplayFileName } from "../../../../functions/helperMethod";
import Loader from "../../../../components/LoadSpinner";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import { useLogin } from "../../../../redux/hooks/useLogin";
import TemplateDetailsDialog from "./TemplateDetailsDialog";
import { getTemplateTooltipContent } from "./templateDetailUtils";

function getContextMenuTypography(theme) {
  return {
    fontFamily: theme.contextMenu?.fontFamily ?? theme.fonts.regular,
    fontSize: theme.contextMenu?.fontSize ?? theme.fontSizes?.menusize ?? 12,
  };
}

function getTemplateRowMenuProps(theme, { onDetails, onDownload, onDownloadToLocal }) {
  const menuType = getContextMenuTypography(theme);
  return (item) => ({
    shouldFocusOnMount: true,
    items: [
      {
        key: "download",
        text: "Download to OneDrive",
        itemProps: {
          styles: {
            root: {
              color: theme.colors.fontColor,
              ...menuType,
            },
          },
        },
        onClick: (ev) => {
          ev?.stopPropagation?.();
          onDownload?.(item);
        },
      },
      {
        key: "downloadLocal",
        text: "Download to local",
        itemProps: {
          styles: {
            root: {
              color: theme.colors.fontColor,
              ...menuType,
            },
          },
        },
        onClick: (ev) => {
          ev?.stopPropagation?.();
          onDownloadToLocal?.(item);
        },
      },
      {
        key: "details",
        text: "Details",
        itemProps: {
          styles: {
            root: {
              color: theme.colors.fontColor,
              ...menuType,
            },
          },
        },
        onClick: (ev) => {
          ev?.stopPropagation?.();
          onDetails?.(item);
        },
      },
    ],
    styles: {
      root: {
        border: `1px solid ${theme.colors.addovation20Green} !important`,
        ...menuType,
        selectors: {
          ".ms-ContextualMenu-link:hover": {
            backgroundColor: theme.colors.contextMenuHover,
            color: theme.colors.fontColor,
          },
          ".ms-ContextualMenu-linkContent": {
            ...menuType,
          },
          ".ms-ContextualMenu-itemText": {
            ...menuType,
          },
          ".ms-FocusZone, .css-134, .ms-ContextualMenu, .is-open, .ms-BaseButton-menuhost": {
            backgroundColor: theme.colors.contextMenuBackground,
          },
        },
      },
    },
  });
}

const getDocClassSortKey = (label = "") => {
  const trimmed = String(label).trim();
  const match = trimmed.match(/^(\d+)/);
  if (match) {
    return [0, parseInt(match[1], 10), trimmed.toLowerCase()];
  }
  return [1, 0, trimmed.toLowerCase()];
};

const groupAndSortTemplates = (templates) => {
  if (!Array.isArray(templates) || templates.length === 0) return [];

  const groups = new Map();
  templates.forEach((tpl) => {
    const key =
      tpl?.DocClassAndDocName ||
      tpl?.DocClass ||
      "Other";
    if (!groups.has(key)) {
      groups.set(key, { label: key, items: [] });
    }
    groups.get(key).items.push(tpl);
  });

  const arr = Array.from(groups.values());
  arr.forEach((g) => {
    g.items.sort((a, b) => {
      const af = String(a?.FormatSize ?? "").toLowerCase();
      const bf = String(b?.FormatSize ?? "").toLowerCase();
      if (af !== bf) return af.localeCompare(bf);
      return String(a?.Title ?? "").localeCompare(String(b?.Title ?? ""));
    });
  });
  arr.sort((a, b) => {
    const ka = getDocClassSortKey(a.label);
    const kb = getDocClassSortKey(b.label);
    for (let i = 0; i < ka.length; i += 1) {
      if (ka[i] < kb[i]) return -1;
      if (ka[i] > kb[i]) return 1;
    }
    return 0;
  });
  return arr;
};

const isWildcardFormatSize = (formatSize) =>
  String(formatSize ?? "").trim() === "*";

/** FormatSize column: show "*" for wildcard templates, otherwise the format code. */
const getTemplateFormatCodeDisplay = (formatSize) => {
  const code = String(formatSize ?? "").trim();
  if (!code) return "";
  if (isWildcardFormatSize(formatSize)) return "*";
  return code;
};

/** Format is often "01C051 - Overtakelse" or "* - Generic"; show only the title part. */
const getTemplateFormatDescription = (formatRaw, formatSize) => {
  const format = getDisplayFileName(formatRaw ?? "");
  const code = String(formatSize ?? "").trim();
  if (!format) return "";

  if (isWildcardFormatSize(formatSize)) {
    const wildcardPrefix = "* - ";
    if (format.startsWith(wildcardPrefix)) {
      return format.slice(wildcardPrefix.length).trim();
    }
    return format.replace(/^\*\s*-\s*/, "").trim() || format;
  }

  if (!code) return format;

  const prefixWithDash = `${code} - `;
  if (format.startsWith(prefixWithDash)) {
    return format.slice(prefixWithDash.length).trim();
  }

  if (format.startsWith(code)) {
    const rest = format.slice(code.length).replace(/^\s*-\s*/, "").trim();
    return rest || format;
  }

  return format;
};

const matchesSearch = (template, group, needle) => {
  if (!needle) return true;
  const hay = [
    group?.label,
    template?.DocClassAndDocName,
    template?.DocClass,
    template?.Format,
    template?.FormatSize,
    template?.Title,
    template?.DocNo,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(needle);
};

const TemplatesList = ({
  templates = [],
  loading = false,
  languageCode = "",
  onTemplateClick,
  onDownload,
  onDownloadToLocal,
  emptyMessage = "No templates found for the selected language.",
}) => {
  const theme = useTheme();
  const styleTheme = useCommonControlsStyle();
  const { teamsContext } = useLogin();

  const [searchValue, setSearchValue] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchHovered, setSearchHovered] = useState(false);
  const [detailTemplate, setDetailTemplate] = useState(null);
  const [selectedRowKey, setSelectedRowKey] = useState(null);
  const [rowMenu, setRowMenu] = useState(null);
  // Groups default to collapsed; entries here mark a group as expanded.
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    setExpandedGroups({});
    setDetailTemplate(null);
    setSelectedRowKey(null);
    setRowMenu(null);
  }, [languageCode]);

  const groups = useMemo(() => groupAndSortTemplates(templates), [templates]);

  const needle = searchValue.trim().toLowerCase();

  const filteredGroups = useMemo(() => {
    if (!needle) return groups;
    return groups
      .map((g) => {
        const groupLabelMatches = String(g.label).toLowerCase().includes(needle);
        const items = groupLabelMatches
          ? g.items
          : g.items.filter((t) => matchesSearch(t, g, needle));
        return { ...g, items };
      })
      .filter((g) => g.items.length > 0);
  }, [groups, needle]);

  const borderGrey = theme.colors.Addovation20Grey ?? "#d8d8d8";
  const focusGreen = theme.colors.addovation70Green ?? "#4c8d6a";
  const fontFamily = theme.fonts?.regular ?? "Poppins, sans-serif";
  const fontColor = theme.colors.fontColor;
  const mutedColor = theme.colors.deactiveColor ?? "#9e9e9e";
  const isDarkMode =
    String(theme.colors.pureColor || "").toLowerCase() === "#000000";
  const rowHoverBg =
    theme.colors.msTeamsFilesHover ?? (isDarkMode ? "#343434" : "#EBEBEB");
  const rowSelectedBg =
    theme.colors.contextMenuHover ?? (isDarkMode ? "#4A4A4A" : "#E2EFDF");
  const menuButtonContrastBg = theme.colors.backgroundColor;
  const templateMenuSize = 20;

  const templateListClassNames = useMemo(
    () =>
      mergeStyleSets({
        menuWrap: {
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: templateMenuSize,
          height: templateMenuSize,
          borderRadius: 2,
          selectors: {
            ":hover": {
              backgroundColor: menuButtonContrastBg,
            },
          },
        },
        menuWrapSelected: {
          backgroundColor: menuButtonContrastBg,
        },
        row: {
          display: "flex",
          alignItems: "center",
          gap: 12,
          width: "100%",
          boxSizing: "border-box",
          padding: "6px 8px 6px 32px",
          cursor: onTemplateClick ? "pointer" : "default",
          borderRadius: 6,
          fontFamily,
          fontSize: 12,
          color: fontColor,
          backgroundColor: "transparent",
          selectors: {
            ":hover": {
              backgroundColor: rowHoverBg,
            },
          },
        },
        rowSelected: {
          backgroundColor: `${rowSelectedBg} !important`,
        },
      }),
    [
      fontColor,
      fontFamily,
      menuButtonContrastBg,
      onTemplateClick,
      rowHoverBg,
      rowSelectedBg,
      templateMenuSize,
    ]
  );

  const templateMenuButtonStyles = useMemo(
    () => ({
      root: {
        color: theme.colors.themeDarkColor,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 0,
        minWidth: 0,
        height: templateMenuSize,
        width: templateMenuSize,
        padding: 0,
        backgroundColor: "transparent",
        selectors: {
          ":hover": {
            backgroundColor: "transparent !important",
          },
          ":active": {
            backgroundColor: "transparent !important",
          },
          "&.is-expanded": {
            backgroundColor: "transparent !important",
          },
        },
      },
      flexContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: templateMenuSize,
        width: templateMenuSize,
        margin: 0,
      },
      menuIcon: {
        fontSize: 12,
        lineHeight: `${templateMenuSize}px`,
        height: templateMenuSize,
        width: templateMenuSize,
        margin: 0,
        padding: 0,
        textAlign: "center",
      },
    }),
    [templateMenuSize, theme.colors.themeDarkColor]
  );

  const scrollListStyles = useMemo(() => {
    const track = theme.colors.scrollbarBackground ?? (isDarkMode ? "#525252" : "#fff");
    const thumb = theme.colors.scrollbarThumb ?? (isDarkMode ? "#848484" : "#ccc");
    const scrollbarSize = 10;

    return {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      overflowX: "hidden",
      scrollbarWidth: "thin",
      scrollbarColor: `${thumb} ${track}`,
      selectors: {
        "&::-webkit-scrollbar": {
          width: scrollbarSize,
        },
        "&::-webkit-scrollbar-track": {
          background: track,
          borderRadius: scrollbarSize,
        },
        "&::-webkit-scrollbar-thumb": {
          background: thumb,
          borderRadius: scrollbarSize,
          border: `2px solid ${track}`,
          minHeight: 40,
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: isDarkMode ? "#9a9a9a" : "#b3b3b3",
        },
        "&::-webkit-scrollbar-button": {
          display: "none",
          height: 0,
          width: 0,
        },
      },
    };
  }, [isDarkMode, theme.colors.scrollbarBackground, theme.colors.scrollbarThumb]);

  // When a search is active, auto-expand matched groups so results are visible.
  const isExpanded = (label) =>
    Boolean(expandedGroups[label]) || Boolean(needle);
  const toggleGroup = (label) => {
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const handleTemplateDetails = useCallback((item) => {
    setDetailTemplate(item);
  }, []);

  const menuProps = useMemo(
    () =>
      getTemplateRowMenuProps(theme, {
        onDetails: handleTemplateDetails,
        onDownload,
        onDownloadToLocal,
      }),
    [theme, handleTemplateDetails, onDownload, onDownloadToLocal]
  );

  const closeRowMenu = useCallback(() => setRowMenu(null), []);

  const renderItem = (item, idx, groupLabel) => {
    const iconPath = getFileIconPath(item?.FileType, item);
    const formatCode = getTemplateFormatCodeDisplay(item?.FormatSize);
    const description = getTemplateFormatDescription(
      item?.Format,
      item?.FormatSize
    );
    const key = `template-row-${groupLabel}-${idx}`;
    const isSelected = selectedRowKey === key;

    return (
      <div
        key={key}
        role={onTemplateClick ? "button" : undefined}
        tabIndex={onTemplateClick ? 0 : undefined}
        onClick={() => onTemplateClick?.(item)}
        onKeyDown={
          onTemplateClick
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onTemplateClick(item);
                }
              }
            : undefined
        }
        className={`${templateListClassNames.row}${
          isSelected ? ` ${templateListClassNames.rowSelected}` : ""
        }`}
      >
        {iconPath ? (
          <Tooltip
            content={
              <div style={{ textAlign: "left" }}>
                {getTemplateTooltipContent(item).map((line, lineIdx) => (
                  <div
                    key={lineIdx}
                    style={{
                      marginBottom: lineIdx < 4 ? 2 : 0,
                      lineHeight: 1.3,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {line}
                  </div>
                ))}
              </div>
            }
            position="right"
            listType="div"
            maxWidth={320}
            delay={100}
          >
            <img
              src={iconPath}
              alt={item?.FileType || "file"}
              style={{ width: 20, height: 20, flexShrink: 0, display: "block" }}
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        ) : (
          <span style={{ width: 20, height: 20, flexShrink: 0 }} />
        )}
        {formatCode ? (
          <span
            style={{
              minWidth: 70,
              color: focusGreen,
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {formatCode}
          </span>
        ) : null}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 16,
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          <span
            style={{
              width: 500,
              minWidth: 500,
              maxWidth: 500,
              color: fontColor,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {description}
          </span>
          <div
            className={`${templateListClassNames.menuWrap}${
              isSelected ? ` ${templateListClassNames.menuWrapSelected}` : ""
            }`}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <IconButton
              menuIconProps={{ iconName: "More" }}
              ariaLabel="Template actions"
              aria-expanded={isSelected}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedRowKey(key);
                if (rowMenu?.key === key) {
                  setRowMenu(null);
                  return;
                }
                setRowMenu({
                  key,
                  item,
                  target: e.currentTarget,
                });
              }}
              styles={templateMenuButtonStyles}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderGroup = (group) => {
    const expanded = isExpanded(group.label);
    const Chevron = expanded ? KeyboardArrowDownIcon : KeyboardArrowRightIcon;
    return (
      <div key={group.label} style={{ marginBottom: 4 }}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => toggleGroup(group.label)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleGroup(group.label);
            }
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 6px",
            cursor: "pointer",
            borderRadius: 6,
            fontFamily,
            fontSize: 13,
            fontWeight: 600,
            color: fontColor,
            userSelect: "none",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = rowHoverBg;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
          aria-expanded={expanded}
        >
          <Chevron sx={{ fontSize: 18, color: fontColor }} />
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {group.label}
          </span>
        </div>
        {expanded && (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {group.items.map((item, idx) => renderItem(item, idx, group.label))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
    <Stack
      tokens={{ childrenGap: 8 }}
      styles={{
        root: {
          width: "100%",
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Stack.Item styles={{ root: { flexShrink: 0 } }}>
        <Label
          style={{
            ...styleTheme.LabelStyle,
            fontWeight: theme.fontWeight?.bold ?? 600,
          }}
        >
          Class / Format / Description
        </Label>
      </Stack.Item>
      <Stack.Item styles={{ root: { flexShrink: 0 } }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            height: 38,
            minHeight: 38,
            boxSizing: "border-box",
            border: `1px solid ${
              searchFocused || searchHovered ? focusGreen : borderGrey
            }`,
            borderRadius: 8,
            padding: "0 6px 0 10px",
            backgroundColor: theme.colors.backgroundColor ?? "#fff",
            transition: "border-color 0.15s ease",
          }}
          onMouseEnter={() => setSearchHovered(true)}
          onMouseLeave={() => setSearchHovered(false)}
        >
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder="Enter your text"
            aria-label="Search templates by class, format, or description"
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontFamily,
              fontSize: 12,
              color: fontColor,
              backgroundColor: "transparent",
              minWidth: 0,
            }}
          />
          {searchValue ? (
            <>
              <ClearIcon
                role="button"
                tabIndex={0}
                aria-label="Clear search"
                onClick={() => setSearchValue("")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSearchValue("");
                  }
                }}
                sx={{
                  fontSize: 16,
                  cursor: "pointer",
                  color: fontColor,
                  opacity: 0.55,
                  outline: "none",
                  paddingRight: "6px",
                  "&:hover": { opacity: 1 },
                  "&:focus, &:focus-visible": { outline: "none" },
                }}
              />
              <div
                style={{
                  width: 1,
                  alignSelf: "stretch",
                  margin: "8px 0",
                  background: borderGrey,
                }}
              />
            </>
          ) : null}
          <SearchIcon sx={{ fontSize: 18, color: focusGreen, padding: "6px", boxSizing: "content-box" }} />
        </div>
      </Stack.Item>
      <Stack.Item grow styles={{ root: scrollListStyles }}>
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "16px 0",
            }}
          >
            <Loader />
          </div>
        ) : filteredGroups.length === 0 ? (
          <div
            style={{
              padding: "12px 4px",
              fontFamily,
              fontSize: 12,
              color: mutedColor,
            }}
          >
            {needle ? "No templates match your search." : emptyMessage}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {filteredGroups.map((g) => renderGroup(g))}
          </div>
        )}
      </Stack.Item>
    </Stack>
    {rowMenu ? (
      <ContextualMenu
        target={rowMenu.target}
        items={menuProps(rowMenu.item).items}
        styles={menuProps(rowMenu.item).styles}
        shouldFocusOnMount
        onDismiss={closeRowMenu}
      />
    ) : null}
    <TemplateDetailsDialog
      template={detailTemplate}
      isOpen={Boolean(detailTemplate)}
      onClose={() => setDetailTemplate(null)}
    />
    </>
  );
};

export default TemplatesList;
